import AgentService from "@/modules/Agent/services/agent.service";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { StatusCodes } from "http-status-codes";
import { ActionContext, ActionTree } from "vuex";
import {
  AgentPricingResponse,
  AgentPricingUpdatePayload,
  IAgentState,
} from "@/core/interfaces/state/agentState.interface";
import { IAgentDetails } from "@/core/interfaces/agent.interface";
import OrganizationService from "@/modules/Organization/services/organization.service";
import { IPaginationMeta } from "@/core/interfaces/state/paginationMeta.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { AgentPermissionsChangeVerificationRequestModel } from "@/modules/Agent/models/agentPermissionsChangeVerification.request";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import analyticsService from "@/analytic.service";
import { AGENT_ANALYTICS_EVENTS } from "@/modules/Agent/constants/analyticsEvents";
import { StoreState } from "@/store/type";

let getOrganizationUsersAbortController: AbortController | undefined;

const actions: ActionTree<IAgentState, StoreState> = {
  async getOrganizationAgents(
    context: ActionContext<IAgentState, StoreState>,
    organizationId: string,
  ) {
    try {
      context.commit("fetchingOrganizationUsers", true);
      const organizationService = new OrganizationService();
      const response: ICoreResponse<any> =
        await organizationService.getOrganizationById(organizationId);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("fetchingOrganizationUsers", false);
        context.commit(
          "saveOrganizationAgents",
          response.data.organization_users,
        );
      } else {
        context.commit("fetchingOrganizationUsers", false);
        throw response;
      }
    } catch (err) {
      context.commit("fetchingOrganizationUsers", false);
    }
  },

  async fetchOrganizationAgents(
    context: ActionContext<IAgentState, StoreState>,
    payload: {
      organizationId: string;
      page_no: number;
      search?: string;
      page_size?: number;
    },
  ) {
    const organizationId = payload.organizationId;
    const pageNo = payload.page_no;
    const pageSize = payload.page_size ?? 10;
    const search = (payload.search ?? "").trim();

    getOrganizationUsersAbortController?.abort();
    getOrganizationUsersAbortController = undefined;

    let aborted = false;
    const controller = new AbortController();
    getOrganizationUsersAbortController = controller;

    try {
      context.commit("fetchingOrganizationUsers", true);

      const organizationService = new OrganizationService();
      const response: ICoreResponse =
        await organizationService.getOrganizationUsers(
          organizationId,
          {
            page_no: pageNo,
            page_size: pageSize,
            search: search || undefined,
          },
          { signal: controller.signal },
        );

      if (response.aborted) {
        aborted = true;
        return;
      }

      if (response.success && response.status === StatusCodes.OK) {
        const data = response.data as {
          users?: IAgentDetails[];
          pagination_meta?: IPaginationMeta;
        };
        const users = data?.users ?? [];
        const paginationMeta = data?.pagination_meta ?? null;

        const isRequestedPageOutOfRange =
          users.length === 0 &&
          paginationMeta !== null &&
          paginationMeta.total_records > 0 &&
          pageNo > paginationMeta.total_pages;

        if (isRequestedPageOutOfRange) {
          context.commit("fetchingOrganizationUsers", false);
          await context.dispatch("fetchOrganizationAgents", {
            organizationId,
            page_no: paginationMeta.total_pages,
            page_size: pageSize,
            search,
          });
          return;
        }

        context.commit("saveOrganizationUsers", users);
        context.commit("setOrganizationAgentsPaginationMeta", paginationMeta);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      context.commit("saveOrganizationUsers", []);
      context.commit("setOrganizationAgentsPaginationMeta", null);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      if (!aborted) {
        context.commit("fetchingOrganizationUsers", false);
      }
    }
  },

  resetOrganizationAgentsPagination(
    context: ActionContext<IAgentState, StoreState>,
  ) {
    context.commit("resetOrganizationAgentsPagination");
  },

  async fetchAgentById(
    context: ActionContext<IAgentState, StoreState>,
    payload: { agentId: string; organizationId: string },
  ) {
    try {
      context.commit("fetchingAgent", true);
      const organizationService = new OrganizationService();
      const response: ICoreResponse<any> =
        await organizationService.getOrganizationAgentById(
          payload.organizationId,
          payload.agentId,
        );
      context.commit("fetchAgentSuccess", response.data);
    } catch (err) {
      context.commit("fetchingAgent", false);
    }
  },

  async updateAgentPermissions(
    context: ActionContext<IAgentState, StoreState>,
    payload: {
      agentId: string;
      callback: (referenceId: string) => void;
      permissions: { [key: string]: boolean };
    },
  ) {
    let response: ICoreResponse<any> = {
      success: false,
    };

    try {
      context.commit("updatingAgentPermissions", true);
      const agentService = new AgentService();
      response = await agentService.updateAgentPermissions(
        context.rootGetters.organizationId,
        payload.agentId,
        payload.permissions,
      );
      if (response.success) {
        context.commit("updateAgentPermissionsSuccess", response.data);
        context.commit("updatingAgentPermissions", false);

        const analyticsPayload = {
          "agent-id": payload.agentId,
          "agent-user-permissions": payload.permissions,
        };

        analyticsService.logActionEvent(
          AGENT_ANALYTICS_EVENTS.UPDATE_PERMISSIONS,
          analyticsPayload,
        );

        payload.callback(response.data?.reference || "");
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      context.commit("updatingAgentPermissions", false);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
  },

  async verificationOnPermissionsChange(
    context: ActionContext<IAgentState, StoreState>,
    payload: {
      body: AgentPermissionsChangeVerificationRequestModel;
      onSuccessCallback: () => void;
      callback: () => boolean;
    },
  ) {
    let response: ICoreResponse<any> = {
      success: false,
    };

    try {
      const agentService = new AgentService();
      response = await agentService.agentPermissionsChangeVerification(
        payload.body,
      );
      if (response.success) {
        context.commit("updateAgentPermissionsSuccess", response.data);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.UPDATE_PERMISSIONS_SUCCESS;
        payload.onSuccessCallback();

        const analyticsPayload = {
          reference: payload.body.reference,
          otp: payload.body.otp,
        };

        analyticsService.logActionEvent(
          AGENT_ANALYTICS_EVENTS.OTP_VERIFICATION,
          analyticsPayload,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    }
    payload.callback();
    notificationService.triggerNotification();
  },

  async getAgentPricing(
    context: ActionContext<IAgentState, StoreState>,
    payload: {
      agentId: string;
    },
  ) {
    let response: ICoreResponse<ICoreResponse<AgentPricingResponse>> = {
      success: false,
    };

    try {
      const agentService = new AgentService();

      response = await agentService.getAgentPricing(payload.agentId);

      if (response.success && response.data) {
        const agentPricingResponse = response.data.data;

        context.commit("saveAgentPricingResponse", agentPricingResponse);

        const analyticsPayload = {
          "agent-id": payload.agentId,
        };

        analyticsService.logActionEvent(
          AGENT_ANALYTICS_EVENTS.GET_AGENT_PRICING,
          analyticsPayload,
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
  },

  async updateAgentPricing(
    context: ActionContext<IAgentState, StoreState>,
    payload: {
      agentId: string;
      data: AgentPricingUpdatePayload;
    },
  ) {
    let response: ICoreResponse<ICoreResponse<AgentPricingResponse>> = {
      success: false,
    };

    try {
      const agentService = new AgentService();

      response = await agentService.updateAgentPricing(
        payload.agentId,
        payload.data,
      );

      if (response.success && response.data) {
        const agentPricingResponse = response.data.data;

        context.commit("updateAgentPricingResponse", agentPricingResponse);

        const analyticsPayload = {
          "agent-id": payload.agentId,
        };

        analyticsService.logActionEvent(
          AGENT_ANALYTICS_EVENTS.GET_AGENT_PRICING,
          analyticsPayload,
        );

        notificationService.type = NOTIFICATION_TYPES.INFO;
        notificationService.description = "Pricing updated successfully";
        notificationService.triggerNotification();
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    }
  },
};

export default actions;
